import { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select, { type SelectOption } from "../../../components/ui/Select";
import { useGetProfileQuery } from "../../../store/api/authApi";
import {
	useCreateMaintenanceRequestMutation,
	type MaintenanceRequestData,
} from "../../../store/api/maintenanceApi";
import { useListPropertiesQuery } from "../../../store/api/propertiesApi";
import { useListUsersQuery } from "../../../store/api/usersApi";

interface CreateMaintenanceModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

const PRIORITY_OPTIONS: SelectOption[] = [
	{ value: "low", label: "Low" },
	{ value: "medium", label: "Medium" },
	{ value: "high", label: "High" },
	{ value: "urgent", label: "Urgent" },
];

const CreateMaintenanceModal: React.FC<CreateMaintenanceModalProps> = ({
	isOpen,
	onClose,
	onSuccess,
}) => {
	const { data: userProfile } = useGetProfileQuery();
	const isAdmin = userProfile?.role === "admin";
	const { data: propertiesData } = useListPropertiesQuery({ page_size: 100 });
	const { data: techniciansData } = useListUsersQuery(
		{ role: "technician", page_size: 100 },
		{ skip: !isAdmin },
	);
	const [createMaintenance, { isLoading }] =
		useCreateMaintenanceRequestMutation();

	const [formData, setFormData] = useState<Partial<MaintenanceRequestData>>({
		organization_id: userProfile?.organization || "",
		property_id: "",
		requested_by_id: userProfile?.id || "",
		title: "",
		description: "",
		priority: "medium",
		location: "",
		estimated_cost: undefined,
		notes: "",
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (isOpen && userProfile) {
			setFormData((prev) => ({
				...prev,
				organization_id: userProfile.organization || "",
				requested_by_id: userProfile.id || "",
			}));
		}
	}, [isOpen, userProfile]);

	const handleInputChange = (
		field: keyof MaintenanceRequestData,
		value: any,
	) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		// Organization is optional for in-house app
		if (!formData.property_id) newErrors.property_id = "Property is required";
		if (!formData.title?.trim()) newErrors.title = "Title is required";
		if (!formData.description?.trim())
			newErrors.description = "Description is required";
		if (!formData.requested_by_id)
			newErrors.requested_by_id = "Requester is required";

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validateForm()) return;

		try {
			await createMaintenance(formData as MaintenanceRequestData).unwrap();
			onSuccess();
			onClose();
		} catch (error: any) {
			console.error("Failed to create maintenance request:", error);
			setErrors({
				submit:
					error?.data?.error ||
					error?.data?.message ||
					"Failed to create maintenance request. Please try again.",
			});
		}
	};

	const handleClose = () => {
		setFormData({
			organization_id: userProfile?.organization || "",
			property_id: "",
			requested_by_id: userProfile?.id || "",
			title: "",
			description: "",
			priority: "medium",
			location: "",
			estimated_cost: undefined,
			notes: "",
			assigned_to_id: undefined,
		});
		setErrors({});
		onClose();
	};

	if (!isOpen) return null;

	const propertyOptions =
		propertiesData?.results.map((prop) => ({
			value: prop.id,
			label: prop.name || prop.address,
		})) || [];

	const technicianOptions =
		techniciansData?.results
			.filter((user) => user.role === "technician")
			.map((tech) => ({
				value: tech.id,
				label: `${tech.first_name} ${tech.last_name}`,
			})) || [];

	return (
		<>
			<div
				className="fixed inset-0 bg-black/50 z-[9998] animate-fade-in"
				onClick={handleClose}
			/>
			<div className="fixed inset-0 z-[9999] flex items-center justify-center p-0 sm:p-4 pointer-events-none">
				<div className="bg-card rounded-lg sm:rounded-lg border border-border w-full h-full sm:h-auto sm:max-w-2xl sm:max-h-[90vh] overflow-hidden animate-scale-in pointer-events-auto flex flex-col">
					<div className="flex items-center justify-between p-4 sm:p-6 border-b border-border flex-shrink-0">
						<h2 className="text-xl font-semibold text-foreground">
							Create Maintenance Request
						</h2>
						<Button variant="ghost" size="icon" onClick={handleClose}>
							<Icon name="X" size={20} />
						</Button>
					</div>

					<form
						onSubmit={handleSubmit}
						className="p-4 sm:p-6 overflow-y-auto flex-1"
					>
						<div className="space-y-6">
							{errors.submit && (
								<div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
									{errors.submit}
								</div>
							)}

							{/* Property */}
							<div>
								<Select
									label="Property"
									options={propertyOptions}
									value={formData.property_id || ""}
									onValueChange={(value) =>
										handleInputChange("property_id", value)
									}
									error={errors.property_id}
									placeholder="Select property"
									required
								/>
							</div>

							{/* Title */}
							<div>
								<Input
									label="Title"
									type="text"
									placeholder="Brief description of the issue"
									value={formData.title || ""}
									onChange={(e) => handleInputChange("title", e.target.value)}
									error={errors.title}
									required
								/>
							</div>

							{/* Description */}
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Description <span className="text-destructive">*</span>
								</label>
								<textarea
									rows={4}
									placeholder="Detailed description of the maintenance issue..."
									value={formData.description || ""}
									onChange={(e) =>
										handleInputChange("description", e.target.value)
									}
									className={`w-full px-3 py-2 border rounded-lg text-foreground bg-background placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none ${
										errors.description ? "border-destructive" : "border-border"
									}`}
									required
								/>
								{errors.description && (
									<p className="text-sm text-destructive mt-1">
										{errors.description}
									</p>
								)}
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{/* Priority */}
								<div>
									<Select
										label="Priority"
										options={PRIORITY_OPTIONS}
										value={formData.priority || "medium"}
										onValueChange={(value) =>
											handleInputChange("priority", value)
										}
									/>
								</div>

								{/* Location */}
								<div>
									<Input
										label="Location"
										type="text"
										placeholder="e.g., Kitchen, Bathroom"
										value={formData.location || ""}
										onChange={(e) =>
											handleInputChange("location", e.target.value)
										}
									/>
								</div>
							</div>

							{/* Assign Technician (Admin only) */}
							{isAdmin && technicianOptions.length > 0 && (
								<div>
									<Select
										label="Assign Technician (Optional)"
										options={[
											{ value: "", label: "Unassigned" },
											...technicianOptions,
										]}
										value={formData.assigned_to_id || ""}
										onValueChange={(value) =>
											handleInputChange("assigned_to_id", value || undefined)
										}
										placeholder="Select technician"
									/>
								</div>
							)}

							{/* Estimated Cost */}
							<div>
								<Input
									label="Estimated Cost (₦)"
									type="number"
									placeholder="0"
									value={formData.estimated_cost?.toString() || ""}
									onChange={(e) =>
										handleInputChange(
											"estimated_cost",
											e.target.value ? parseInt(e.target.value) : undefined,
										)
									}
									min="0"
								/>
							</div>

							{/* Notes */}
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Additional Notes
								</label>
								<textarea
									rows={3}
									placeholder="Any additional information..."
									value={formData.notes || ""}
									onChange={(e) => handleInputChange("notes", e.target.value)}
									className="w-full px-3 py-2 border border-border rounded-lg text-foreground bg-background placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
								/>
							</div>
						</div>

						<div className="flex items-center justify-end gap-2 sm:gap-3 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-border flex-shrink-0">
							<Button
								type="button"
								variant="outline"
								onClick={handleClose}
								disabled={isLoading}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isLoading}>
								{isLoading ? (
									<>
										<Icon
											name="Loader2"
											size={16}
											className="mr-2 animate-spin"
										/>
										Creating...
									</>
								) : (
									<>
										<Icon name="Plus" size={16} className="mr-2" />
										Create
									</>
								)}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default CreateMaintenanceModal;
